import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { getLocalizedField } from '@/utils/helpers';
import { translateText } from '@/services/translateService';

// 需要自动翻译的语言
const AUTO_TRANSLATE_LANGS = ['ru', 'uk', 'fr', 'es', 'pt', 'ja', 'ko', 'ar', 'uz'];

const AboutSection = ({ aboutUs }) => {
  const { t, i18n } = useTranslation();
  const [translatedTitle, setTranslatedTitle] = useState('');
  const [translatedDesc, setTranslatedDesc] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  
  const currentLang = i18n.language;
  const needsTranslation = AUTO_TRANSLATE_LANGS.includes(currentLang);
  
  // 获取原始文本
  const sourceTitle = aboutUs?.title_en || aboutUs?.title || '';
  const sourceDesc = aboutUs?.description_en || aboutUs?.description || '';
  
  useEffect(() => {
    // 如果不需要翻译，清空翻译状态
    if (!needsTranslation || !aboutUs) {
      setTranslatedTitle('');
      setTranslatedDesc('');
      return;
    }
    
    // 自动翻译
    const translate = async () => {
      setIsTranslating(true);
      try {
        const [title, desc] = await Promise.all([
          sourceTitle ? translateText(sourceTitle, currentLang) : '',
          sourceDesc ? translateText(sourceDesc, currentLang) : ''
        ]);
        setTranslatedTitle(title);
        setTranslatedDesc(desc);
      } catch (err) {
        console.error('Translation error:', err);
      } finally {
        setIsTranslating(false);
      }
    };
    
    translate();
  }, [aboutUs, currentLang, needsTranslation, sourceTitle, sourceDesc]);
  
  // 确定显示的内容
  const displayTitle = needsTranslation && translatedTitle 
    ? translatedTitle 
    : getLocalizedField(aboutUs, 'title', currentLang) || t('about.defaultTitle', 'ArtCanvas');
    
  const displayDesc = needsTranslation && translatedDesc
    ? translatedDesc
    : getLocalizedField(aboutUs, 'description', currentLang) || t('about.defaultDesc');

  return (
    <section id="about" className="py-16 bg-gradient-to-br from-amber-50 to-orange-50/50" data-testid="about-section">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="bg-amber-100 text-amber-700 mb-4">{t('about.title')}</Badge>
            <h2 className="text-3xl font-bold text-amber-900 mb-4">
              {displayTitle}
              {isTranslating && <span className="ml-2 text-amber-500 text-sm animate-pulse">...</span>}
            </h2>
            <p className="text-amber-800/80 leading-relaxed">
              {displayDesc}
            </p>
          </div>
          <div className="relative">
            <img
              src={aboutUs?.image_url || 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=600'}
              alt={t('about.title')}
              className="rounded-2xl shadow-xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
